import { FormField, Select } from '@amzn/awsui-components-react-v3';
import { ActivityLMSType, ActivityViltType } from '../../Common/Common';
import { ActivityData } from '../../../../interfaces/activity';
import { useEffect, useState } from 'react';
import { SelectProps } from '@amzn/awsui-components-react-v3';

const OPTIONAL_DESCRIPTION =
    'This is a read only field. Selecting a LMS type will set this value.';

export type ViltTypeValues =
    | ActivityViltType.Necto
    | ActivityViltType.WebEx
    | ActivityViltType.Other;

interface ViltTypeOption extends SelectProps.Option {
    readonly label: ViltTypeValues;
    readonly value: string;
}

interface SelectVILTTypeProps {
    handleFieldEvent: (values: Partial<ActivityData>) => void;
    formValues: {
        lms_type: string;
        v_ilt_type?: string | null;
        classrooms_arn?: string | null;
        classrooms_status?: string | null;
        activity_status?: string;
    };
    errors: {
        lms_type: string;
        v_ilt_type: string;
        classrooms_arn?: string;
        classrooms_status?: string;
    };
    isDisabled?: boolean;
}

const SelectVILTType = ({
    handleFieldEvent,
    formValues,
    errors,
    isDisabled = false,
}: SelectVILTTypeProps) => {
    // Note: Previously this function disabled editing when classrooms_arn and
    // classrooms_status were both set. This restriction was removed because:
    // 1. Backend now handles classroom re-creation when VILT type changes
    // 2. Operators need to change to "Other" type for Zoom/Teams meetings
    // The classrooms_arn and classrooms_status parameters are kept for interface
    // compatibility but no longer affect the disabled state.

    const getOptionsList = () => {
        let options: SelectProps.Option[] = [];
        const lmsType = formValues.lms_type;

        // Enable "Other" option for all LMS types to support manual VILT configuration
        // for platforms like Zoom and Teams (VILT_Other_Type requirement)
        // Include "None" LMS type to allow programs like Dedicated Cloud to manage
        // their own vILT meetings without auto-provisioned WebEx
        if (
            lmsType === ActivityLMSType.Kiku ||
            lmsType === ActivityLMSType.SkillBuilder ||
            lmsType === ActivityLMSType.MyClass ||
            lmsType === ActivityLMSType.CustomerLMS ||
            lmsType === ActivityLMSType.None
        ) {
            options = [
                {
                    value: ActivityViltType.WebEx,
                    label: ActivityViltType.WebEx,
                },
                {
                    value: ActivityViltType.Other,
                    label: ActivityViltType.Other,
                },
            ];
        }
        return options;
    };

    const [viltTypeOptions, setViltOptions] = useState(getOptionsList());

    const viltTypeOptionsLookup = viltTypeOptions.reduce(
        (acc, opt) => {
            acc[opt.label] = opt;
            return acc;
        },
        {} as {
            [key in ViltTypeValues]: ViltTypeOption;
        },
    );

    useEffect(() => {
        setViltOptions(getOptionsList());
    }, [formValues.v_ilt_type, formValues.lms_type]);
    return (
        <FormField
            label="vILT System Type"
            errorText={errors?.v_ilt_type}
            description={viltTypeOptions.length < 2 ? OPTIONAL_DESCRIPTION : ''}
            data-testid="formField-EditVILTType"
        >
            <Select
                options={viltTypeOptions}
                placeholder="Select vILT System Type"
                selectedOption={
                    formValues.v_ilt_type
                        ? viltTypeOptionsLookup[
                              formValues.v_ilt_type as ViltTypeValues
                          ]
                        : null
                }
                onChange={(e) => {
                    handleFieldEvent({
                        v_ilt_type: e.detail.selectedOption.label,
                    });
                }}
                data-testid={`EditVILTType`}
                disabled={isDisabled || viltTypeOptions.length < 2}
            />
        </FormField>
    );
};

export default SelectVILTType;
